import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import Select from '../../common/Form/Select';
import Input from '../../common/Form/Input';
import { actualValueForDropdown, debounce } from '../../utils/common';
import { getOrgContacts } from '../../actions/org';
import { CLEAR_ORG_CONTACTS_DROPDOWN_LIST_DATA } from '../../types/org';

const RenderContact = ({ name, label, onChange = () => { }, org_id, className = 'grid grid-cols-2 gap-2', required = false, prefill = [], prefillObj = [], isMulti = false, readOnly = false, cleanup = true }) => {
    const methods = useFormContext();
    const dispatch = useDispatch();
    const orgContactsDropdown = useSelector((state) => state.org.orgContactsDropdown);
    const isOrgContactsLoading = useSelector((state) => state.loading.isOrgContactsLoading);

    const memoizedOrgContactsDropdown = useMemo(() => {
        let d = [];
        if (orgContactsDropdown?.[org_id]) {
            d = [...d, ...orgContactsDropdown[org_id]];
        }
        return d;
    }, [orgContactsDropdown, org_id]);

    const getOrgContactsList = ({ limit = 100, page = 1, org_id, query = '' }) => {
        dispatch(getOrgContacts({ limit, page, orgId: org_id, query, dropdown: true }));
    };

    const debouncedOrgContactsSearch = useCallback(debounce(getOrgContactsList), []);

    useEffect(() => {
        if (org_id) {
            getOrgContactsList({ org_id });
            methods.setValue('project_contact_person', null);
        }
    }, [org_id]);

    useEffect(() => {
        if (cleanup) {
            return () => {
                dispatch({ type: CLEAR_ORG_CONTACTS_DROPDOWN_LIST_DATA });
            }
        }
    }, [dispatch, cleanup]);

    const hasPrefilled = React.useRef(false);

    useEffect(() => {
        if (prefillObj && Object.keys(prefillObj).length > 0 && !hasPrefilled.current) {
            methods.setValue(name, prefillObj);
            hasPrefilled.current = true;
        }
    }, [prefillObj]);

    useEffect(() => {
        if (!hasPrefilled.current && prefill && prefill.length > 0 && memoizedOrgContactsDropdown && memoizedOrgContactsDropdown.length > 0) {
            const contact = memoizedOrgContactsDropdown.filter(contact => prefill.includes(contact.id));
            if (contact && contact.length > 0) {
                methods.setValue(name, contact);
                hasPrefilled.current = true;
            }
        }
    }, [prefill, memoizedOrgContactsDropdown]);

    const isNewContact = actualValueForDropdown(methods.watch(name)) === 'add-new-contact';

    return <>
        <Select
            label={label}
            name={name}
            options={[
                { label: 'Add New Contact', value: 'add-new-contact', key: 'add_new_contact' },
                ...(Array.isArray(memoizedOrgContactsDropdown) ? memoizedOrgContactsDropdown : [])
            ]}
            isLoading={isOrgContactsLoading}
            required={required}
            onInputChange={(event) => {
                if (event.length > 0) {
                    debouncedOrgContactsSearch({ query: event, org_id })
                }
            }}
            onChange={(event) => {
                const value = event?.value;
                onChange(value)
            }}
            isMulti={isMulti}
            readOnly={readOnly}
        />
        {isNewContact && <>
            <Input shouldUnregister label='Name' name={`${name}:name`} type={'text'} />
            <Input
                shouldUnregister
                label='Mobile'
                type={'number'}
                name={`${name}:mobile`}
                validate={(mobile) => {
                    if (mobile === '') return true;
                    return mobile.length === 10 || 'Invalid Mobile';
                }}
            />
            <Input
                shouldUnregister
                label='Email'
                type={'text'}
                name={`${name}:email`}
                validate={(email) => {
                    if (email === '') return true;
                    return (
                        email.match(
                            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                        ) || 'Invalid Email'
                    );
                }}
            />
        </>}
    </>
}

export default RenderContact;
